import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from './utils/Utils.js';
import Sidebar from './layout/Sidebar.js';
import BackButton from './layout/BackButton.js';
import { FORM_SCREENS } from './layout/useHubScreen.js';
import Overview from './home/Overview.js';
import Charts from './charts/Charts.js';
import HerdHub from './herd/HerdHub.js';
import HerdLotDetail from './herd/lots/HerdLotDetail.js';
import MessagesHub from './whatsapp/MessagesHub.js';
import ClimateHub from './climate/ClimateHub.js';
import ReproductionHub from './reproduction/ReproductionHub.js';
import ReproLotDetail from './reproduction/lots/ReproLotDetail.js';
import LotNotes from './shared/lots/LotNotes.js';
import LotProtocol from './shared/lots/LotProtocol.js';
import RanchSettingsHub from './ranch-settings/RanchSettingsHub.js';
import AccountHub from './account/AccountHub.js';
import Weight from './weight/Weight.js';
import WeightLotDetail from './weight/lots/WeightLotDetail.js';
import WeightLotSessions from './weight/lots/WeightLotSessions.js';
import WeightLotScores from './weight/lots/WeightLotScores.js';
import WeightLotAnimalDetail from './weight/lots/WeightLotAnimalDetail.js';
import WeightAnimalDetail from './weight/animals/WeightAnimalDetail.js';
import WeightAnimalWeighins from './weight/animals/WeightAnimalWeighins.js';
import Finances from './finances/Finances.js';
import ProjectDetail from './finances/projects/ProjectDetail.js';
import ListsHub from './lists/ListsHub.js';
import EditListItem from './lists/EditListItem.js';
import Profile from './profile/Profile.js';
import ChangePassword from './profile/ChangePassword.js';
import Billing from './billing/Billing.js';
import ContactUs from './contact/ContactUs.js';
import { MobileNavProvider } from './layout/MobileNavContext';
import { HintsProvider, useHints } from './layout/HintsContext';
import FirstTimeSetup from './setup/FirstTimeSetup.js';

// Daily-use tabs shown in the main tab bar
const TABS = [
  { key: "births",       path: "/herd" },
  { key: "reproduction", path: "/reproduction" },
  { key: "weight",       path: "/weight" },
  { key: "climate",      path: "/climate" },
  { key: "finances",     path: "/finances" },
  { key: "lists",        path: "/lists" },
  { key: "charts",       path: "/charts" },
];

// Settings tabs hidden behind the gear icon
const SETTINGS_TABS = [
  { key: "ranch_settings", path: "/ranch-settings" },
  { key: "whatsapp",       path: "/messages" },
  { key: "account",        path: "/account" },
  { key: "contact_us",     path: "/contact" },
];

// Sub-tab URL slugs for each main tab (by TABS index)
const SUB_PATHS = {
  0: ['animals', 'lots', 'plans', 'settings'],
  1: ['animals', 'lots', 'bulls', 'protocols', 'settings'],
  2: ['animals', 'lots', 'slaughter', 'plans', 'settings'],
  3: ['rain', 'temperatures'],
  4: ['transactions', 'projects', 'charts', 'categories', 'institutions'],
  5: ['all', 'categories'],
};

// Sub-nav i18n keys for the hamburger menu (by TABS index)
const SUB_NAV_CONFIG = {
  0: ["animals", "herd_lots", "plans", "settings"],
  1: ["animals", "lots", "bulls", "protocols", "settings"],
  2: ["animals", "weight_lots", "slaughter_tab", "plans", "settings"],
  3: ["rain", "temperatures"],
  4: ["transactions", "projects", "charts", "categories", "institutions"],
  5: ["lists_all", "lists_categories"],
};

// Sub-tab URL slugs for settings tabs (by SETTINGS_TABS index)
const SETTINGS_SUB_PATHS = {
  0: ['team', 'areas', 'breeds', 'death-causes', 'finance-currency', 'lot-fields'],
  1: ['samples', 'try-it', 'log', 'phone-number'],
  2: ['billing', 'ranches'],
};

// Sub-nav i18n keys for settings tabs
const SETTINGS_SUB_NAV_CONFIG = {
  0: ["team", "areas", "breeds", "death_causes", "currency", "lot_fields"],
  1: ["samples", "try_it", "log", "phone_number"],
  2: ["billing", "ranches"],
};

// Add/Upload screens inside a hub's sub-tab (e.g. /herd/animals/add) get their
// own URL so Back closes the form (see layout/useHubScreen.js). Exact paths,
// so they win over detail routes like /herd/lots/:lotId.
const formRoutes = (basePath, subPaths, element) =>
  subPaths.flatMap(sub => FORM_SCREENS.map(screen => (
    <Route key={`${basePath}/${sub}/${screen}`} path={`${basePath}/${sub}/${screen}`} element={element} />
  )));

const Dashboard = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [billingBanner, setBillingBanner] = useState(null); // 'success' | 'cancel' | null
  const [ranchName, setRanchName] = useState(localStorage.getItem('zapmanejo_ranch_name') || '');
  const [ranches, setRanches] = useState([]);
  const [setupRequired, setSetupRequired] = useState(null);

  const fetchRanches = () => {
    Fetch('/api/ranches', 'GET', null, (data) => {
      const list = data || [];
      setRanches(list);
      if (list.length === 0) {
        setSetupRequired(true);
        return;
      }
      setSetupRequired(false);
      const storedID = localStorage.getItem('zapmanejo_ranch_id');
      const current = (storedID && list.find(r => r._id === storedID)) || list[0];
      if (current) {
        localStorage.setItem('zapmanejo_ranch_id', current._id);
        localStorage.setItem('zapmanejo_ranch_name', current.name || '');
        setRanchName(current.name || '');
      }
    }, () => {});
  };

  useEffect(() => {
    fetchRanches();

    const onRanchUpdated = (e) => setRanchName(e.detail?.name || '');
    window.addEventListener('ranch-updated', onRanchUpdated);
    return () => window.removeEventListener('ranch-updated', onRanchUpdated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRanchSwitch = (ranchID) => {
    localStorage.setItem('zapmanejo_ranch_id', ranchID);
    window.location.reload();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const billing = params.get('billing');
    if (billing === 'success' || billing === 'cancel') {
      setBillingBanner(billing);
      // Clean the query param from the URL without triggering a navigation
      navigate(location.pathname, { replace: true });
      // Auto-dismiss after 8 seconds
      const timer = setTimeout(() => setBillingBanner(null), 8000);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine active tab index from current URL
  const activeTab = TABS.findIndex(t => location.pathname.startsWith(t.path));

  const onTabClicked = (id) => navigate(TABS[id].path);

  const onSubTabClicked = (mainTabIndex, subTabIndex) => {
    const basePath = TABS[mainTabIndex].path;
    const subPath = SUB_PATHS[mainTabIndex]?.[subTabIndex];
    navigate(subPath ? `${basePath}/${subPath}` : basePath);
  };

  const onSettingsTabClicked = (index) => navigate(SETTINGS_TABS[index].path);

  const onSettingsSubTabClicked = (settingsTabIndex, subTabIndex) => {
    const basePath = SETTINGS_TABS[settingsTabIndex].path;
    const subPath = SETTINGS_SUB_PATHS[settingsTabIndex]?.[subTabIndex];
    navigate(subPath ? `${basePath}/${subPath}` : basePath);
  };

  const onClickLogo = () => navigate('/');
  const onClickProfile = () => navigate('/profile');
  const onClickChangePassword = () => navigate('/change-password');

  const navigateTo = (destination) => {
    if      (destination === 'addBull')   navigate('/reproduction/bulls/add');
    else if (destination === 'addTeam')   navigate('/ranch-settings/team/add');
    else if (destination === 'addArea')   navigate('/ranch-settings/areas/add');
    else if (destination === 'addBreed')  navigate('/ranch-settings/breeds/add');
    else if (destination === 'whatsapp')  navigate('/messages');
    else if (destination === 'addList')   navigate('/lists/all/new');
    else if (destination === 'herdSexNotSet') navigate('/herd/animals', { state: { sexFilter: 'unset' } });
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { hintsVisible, showHints, hideHints } = useHints();

  if (setupRequired === true) {
    return <FirstTimeSetup onSuccess={fetchRanches} />;
  }

  return (
    <MobileNavProvider>
    <div className='Dashboard'>
      <Sidebar
        user={props.user}
        ranchName={ranchName}
        ranches={ranches}
        onRanchSwitch={handleRanchSwitch}
        tabs={TABS}
        activeTab={activeTab}
        onTabClicked={(i) => { onTabClicked(i); }}
        settingsTabs={SETTINGS_TABS}
        activeSettingsTab={SETTINGS_TABS.findIndex(t => location.pathname.startsWith(t.path))}
        onSettingsTabClicked={(i) => { onSettingsTabClicked(i); }}
        subNavConfig={SUB_NAV_CONFIG}
        subPaths={SUB_PATHS}
        onSubTabClicked={onSubTabClicked}
        settingsSubNavConfig={SETTINGS_SUB_NAV_CONFIG}
        settingsSubPaths={SETTINGS_SUB_PATHS}
        onSettingsSubTabClicked={onSettingsSubTabClicked}
        onClickLogo={onClickLogo}
        onClickProfile={onClickProfile}
        onClickChangePassword={onClickChangePassword}
        hintsVisible={hintsVisible}
        onToggleHints={() => hintsVisible ? hideHints() : showHints()}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className='main-content'>
        <div className='mobile-top-bar'>
          <button className='mobile-hamburger' onClick={() => setSidebarOpen(true)}>☰</button>
          <span className='mobile-top-bar-brand'>ZapManejo</span>
          <BackButton />
        </div>
        {billingBanner && (
          <div className={`billing-banner billing-banner-${billingBanner}`}>
            <span>{billingBanner === 'success' ? t('billing_success_msg') : t('billing_cancel_msg')}</span>
            <button className="billing-banner-close" onClick={() => setBillingBanner(null)}>✕</button>
          </div>
        )}
        <div className='content-area'>
          <Routes>
            {formRoutes('/herd',           SUB_PATHS[0],          <HerdHub />)}
            {formRoutes('/reproduction',   SUB_PATHS[1],          <ReproductionHub />)}
            {formRoutes('/weight',         SUB_PATHS[2],          <Weight />)}
            {formRoutes('/climate',        SUB_PATHS[3],          <ClimateHub />)}
            {formRoutes('/finances',       SUB_PATHS[4],          <Finances />)}
            {formRoutes('/ranch-settings', SETTINGS_SUB_PATHS[0], <RanchSettingsHub />)}
            {formRoutes('/account',        SETTINGS_SUB_PATHS[2], <AccountHub />)}
            {formRoutes('/lists',          ['categories'],        <ListsHub />)}
            {/* Protocol edit: /…/:recordId/edit (useHubScreen). */}
            <Route path="/herd/plans/:recordId/edit"              element={<HerdHub />} />
            <Route path="/reproduction/protocols/:recordId/edit"  element={<ReproductionHub />} />
            <Route path="/weight/plans/:recordId/edit"            element={<Weight />} />
            {/* Lists: new list, a list's items, add item (lists/listsPath.js). */}
            <Route path="/lists/all/new"                          element={<ListsHub />} />
            <Route path="/lists/all/:listId"                      element={<ListsHub />} />
            <Route path="/lists/all/:listId/add"                  element={<ListsHub />} />
            {/* Old per-type URLs (before Trello UMndrjdk), kept for bookmarks. */}
            <Route path="/lists/general/*"                        element={<Navigate to="/lists/all" replace />} />
            <Route path="/lists/food/*"                           element={<Navigate to="/lists/all" replace />} />
            <Route path="/"                        element={<Overview navigateTo={navigateTo} />} />
            <Route path="/herd"                    element={<Navigate to="/herd/animals" replace />} />
            <Route path="/herd/lots/:lotId"                  element={<HerdLotDetail />} />
            <Route path="/herd/lots/:lotId/notes"            element={<LotNotes />} />
            <Route path="/herd/lots/:lotId/protocol"         element={<LotProtocol lotType="herd" />} />
            <Route path="/herd/:subTab"                      element={<HerdHub />} />
            <Route path="/charts"                  element={<Charts />} />
            <Route path="/profile"                 element={<Profile />} />
            <Route path="/change-password"         element={<ChangePassword />} />
            <Route path="/reproduction"                              element={<Navigate to="/reproduction/animals" replace />} />
            <Route path="/reproduction/lots/:lotId"                  element={<ReproLotDetail />} />
            <Route path="/reproduction/lots/:lotId/notes"            element={<LotNotes />} />
            <Route path="/reproduction/lots/:lotId/protocol"         element={<LotProtocol lotType="reproduction" />} />
            <Route path="/reproduction/:subTab"                      element={<ReproductionHub />} />
            <Route path="/climate"                 element={<Navigate to="/climate/rain" replace />} />
            <Route path="/climate/:subTab"         element={<ClimateHub />} />
            <Route path="/weight"                  element={<Navigate to="/weight/animals" replace />} />
            <Route path="/weight/lots/:lotId"                element={<WeightLotDetail />} />
            <Route path="/weight/lots/:lotId/weights"       element={<WeightLotSessions />} />
            <Route path="/weight/lots/:lotId/notes"         element={<LotNotes />} />
            <Route path="/weight/lots/:lotId/protocol"      element={<LotProtocol lotType="weight" />} />
            <Route path="/weight/lots/:lotId/scores"        element={<WeightLotScores />} />
            <Route path="/weight/lots/:lotId/animals/:tag"               element={<WeightLotAnimalDetail />} />
            <Route path="/weight/animals/:tag"             element={<WeightAnimalDetail />} />
            <Route path="/weight/animals/:tag/weighins"    element={<WeightAnimalWeighins />} />
            <Route path="/weight/:subTab"                   element={<Weight />} />
            <Route path="/finances"               element={<Navigate to="/finances/transactions" replace />} />
            <Route path="/finances/projects/:projectId" element={<ProjectDetail />} />
            <Route path="/finances/:subTab"        element={<Finances />} />
            <Route path="/lists"                   element={<Navigate to="/lists/all" replace />} />
            <Route path="/lists/items/:id"         element={<EditListItem />} />
            <Route path="/lists/:subTab"            element={<ListsHub />} />
            <Route path="/ranch-settings"          element={<Navigate to="/ranch-settings/team" replace />} />
            <Route path="/ranch-settings/:subTab"  element={<RanchSettingsHub />} />
            <Route path="/messages"                element={<Navigate to="/messages/samples" replace />} />
            <Route path="/messages/:subTab"        element={<MessagesHub />} />
            <Route path="/account"                 element={<Navigate to="/account/billing" replace />} />
            <Route path="/account/:subTab"         element={<AccountHub />} />
            <Route path="/contact"                 element={<ContactUs />} />
            <Route path="*"                        element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
    </MobileNavProvider>
  );
}

const DashboardWithHints = (props) => (
  <HintsProvider>
    <Dashboard {...props} />
  </HintsProvider>
);

export default DashboardWithHints;
